import { defineStore } from 'pinia';

import type { Call, Email, EmailThread, Partner, Task } from '@stuut/api/models';
import type { TaskQueryParams } from '@stuut/api/query';

import { stuut } from '@/api';
import { useStatusCounts } from '@/store/statusCounts';

interface State {
  calls: Call[];
  currentTask?: Task;
  emails: Email[];
  emailThreads: EmailThread[];
  loading: boolean;
  loadingEmails: Record<string, boolean>;
  loadingPartners: Record<string, boolean>;
  myStuutTaskCount: number;
  myStuutTasks: Task[];
  partners: Partner[];
  sortOrder: string;
  tasks: Task[];
  tasksTable: Task[];
}

export const useTaskStore = defineStore('tasks', {
  actions: {
    async fetchTasks(params?: TaskQueryParams): Promise<any> {
      const taskList = await stuut.tasks.list({ params });

      taskList?.forEach((task) => {
        const existingTaskIndex = this.findTaskIndex(task);

        if (existingTaskIndex !== -1) {
          this.tasks[existingTaskIndex] = task;
        } else {
          this.tasks?.push(task);
        }

        return task;
      });

      return taskList;
    },

    findTaskIndex(task: Task) {
      return this.tasks?.findIndex((existingTask) => existingTask.id === task?.id);
    },

    async peekFindCall(callId: string) {
      const call = this.calls.find((e) => e.id === callId);

      if (call) return call;

      return stuut.calls.retrieve(callId);
    },

    async peekFindEmail(emailId: string) {
      const email = this.emails.find((e) => e.id === emailId);

      if (email) return email;

      if (this.loadingEmails[emailId]) return;

      try {
        this.loadingEmails = { ...this.loadingEmails, [emailId]: true };

        const response = await stuut.emails.retrieve(emailId);

        this.emails.push(response);

        return response;
      } catch (err: any) {
        console.error(err.clientMessage);
      } finally {
        this.loadingEmails = { ...this.loadingEmails, [emailId]: false };
      }
    },

    async peekFindEmailThread(emailThreadId: string) {
      const emailThread = this.emailThreads.find((e) => e.id === emailThreadId);

      if (emailThread) return emailThread;

      return stuut.emailThreads.retrieve(emailThreadId);
    },

    async peekFindPartner(partnerId: string) {
      const partner = this.partners.find((e) => e.id === partnerId);

      if (partner) return partner;

      if (this.loadingPartners[partnerId]) return;

      try {
        this.loadingPartners = { ...this.loadingPartners, [partnerId]: true };

        const response = await stuut.partners.retrieve(partnerId);

        this.partners.push(response);

        return response;
      } catch (err: any) {
        console.error(err.clientMessage);
      } finally {
        this.loadingPartners = { ...this.loadingPartners, [partnerId]: false };
      }
    },

    async peekFindRecord(taskId: string) {
      const task = await this.tasks?.find((t) => t?.id === taskId);

      if (task?.id) return task;

      this.loading = true;

      const response = stuut.tasks.retrieve(taskId);

      this.loading = false;

      return response;
    },

    setCurrentTask(task?: Task) {
      this.currentTask = task as Task;
      return task;
    },

    setMyStuutTasks(myStuutTasks?: Task[], myStuutTaskCount?: number) {
      this.$patch({ myStuutTaskCount, myStuutTasks });
      useStatusCounts().invalidate('tasks');
    },

    updateAllTasks(task: Task) {
      const tableIndex = this.tasksTable.findIndex((t) => t.id === task?.id);
      const myStuutIndex = this.myStuutTasks.findIndex((t) => t.id === task?.id);
      const index = this.tasks.findIndex((t) => t.id === task?.id);
      const patchUpdates: Partial<State> = {};

      if (tableIndex > -1) {
        const newTasksTable = [...this.tasksTable];

        newTasksTable[tableIndex] = task;
        patchUpdates.tasksTable = newTasksTable;
      }

      if (myStuutIndex > -1) {
        const newMyStuutTasks = [...this.myStuutTasks];

        newMyStuutTasks[myStuutIndex] = task;
        patchUpdates.myStuutTasks = newMyStuutTasks;
      }

      if (index > -1) {
        const newTasks = [...this.tasks];

        newTasks[index] = task;
        patchUpdates.tasks = newTasks;
      }

      this.$patch(patchUpdates);

      useStatusCounts().invalidate('tasks');
    },
  },

  getters: {
    currentTaskIndex(state): number {
      return this.myStuutTasks?.findIndex(
        (existingTask) => existingTask.id === state.currentTask?.id,
      );
    },

    getById: (state) => {
      return (taskId: string) =>
        state.tasksTable?.find((task) => task.id === taskId) as Task | undefined;
    },

    getFromTableById: (state) => {
      return (taskId: string) => state.tasksTable.find((task) => task.id === taskId) as Task;
    },

    getFromTasksById: (state) => {
      return (taskId: string) => state.tasks.find((task) => task.id === taskId) as Task;
    },

    nextTask(): Task {
      return this.myStuutTasks?.[this.nextTaskIndex];
    },

    nextTaskIndex(): number {
      const nextIndex = this.currentTaskIndex + 1;

      if (nextIndex <= this.myStuutTasks?.length - 1) return nextIndex;

      return 0;
    },

    openMyStuutTasks(state): Task[] {
      return state.myStuutTasks.filter((task) => task?.status === 'open');
    },

    openTasks(state): Task[] {
      return state.tasks?.filter((task) => task?.status === 'open');
    },

    previousTask(): Task {
      return this.myStuutTasks?.[this.previousTaskIndex];
    },

    previousTaskIndex(): number {
      const previousIndex = this.currentTaskIndex - 1;

      if (previousIndex >= 0) return previousIndex;

      if (this.currentTaskIndex !== this.myStuutTasks?.length - 1) {
        return this.myStuutTasks?.length - 1;
      }

      return 0; // Default return value
    },
  },

  state: (): State => ({
    calls: [],
    currentTask: undefined,
    emails: [],
    emailThreads: [],
    loading: false,
    loadingEmails: {},
    loadingPartners: {},
    myStuutTaskCount: 0,
    myStuutTasks: [],
    partners: [],
    sortOrder: 'desc',
    tasks: [],
    tasksTable: [],
  }),
});

export default useTaskStore;
